import React from 'react';
import PropTypes from 'prop-types';
import { Button, Tag, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import { Box, Text } from 'atoms';
import {
	BasicInformationStep,
	BusinessInformationStep,
	BankingInformationStep,
	VehicleInformationStep,
	ServiceabilityInfoStep,
} from './vendor-steps';

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
		padding-bottom: 4px;
	}

	.ant-form-item-label > label {
		font-size: 14px;
		font-weight: 500;
		color: #1c1c1c;
		height: auto;
	}

	.ant-input,
	.ant-select-selector {
		border-radius: 4px;
		font-size: 14px;
	}

	.ant-input::placeholder,
	.ant-select-selection-placeholder {
		color: #bfbfbf;
		font-size: 13px;
	}
`;

const ButtonContainer = styled(Box)`
	display: flex;
	justify-content: space-between;
	margin-top: 40px;
	padding-top: 24px;
	border-top: 1px solid #e0e0e0;
`;

const PreviousButton = styled(Button)`
	border: 1px solid #d9d9d9;
	color: #595959;
	border-radius: 4px;
	padding: 8px 32px;
	height: auto;
	font-size: 15px;
	font-weight: 500;

	&:hover {
		border-color: #18405d;
		color: #18405d;
	}
`;

const NextButton = styled(Button)`
	background-color: #18405d;
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 32px;
	height: auto;
	font-size: 15px;
	font-weight: 500;

	&:hover {
		background-color: #153e60;
		color: #fff;
	}
`;

const SaveButton = styled(Button)`
	background-color: #fff;
	color: #18405d;
	border: 1px solid #18405d;
	border-radius: 4px;
	padding: 8px 32px;
	height: auto;
	font-size: 15px;
	font-weight: 500;

	&:hover {
		background-color: #f0f7ff;
		border-color: #153e60;
		color: #153e60;
	}
`;

const StepHeader = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 1px solid #e0e0e0;
`;

const StepTitle = styled(Text)`
	font-size: 18px;
	font-weight: 600;
	color: #1c1c1c;
`;

const EditButton = styled(Button)`
	display: flex;
	align-items: center;
	gap: 6px;
	border-color: #18405d;
	color: #18405d;
	border-radius: 4px;
	padding: 6px 16px;
	height: auto;
	font-size: 14px;
	font-weight: 500;

	&:hover {
		border-color: #153e60;
		color: #153e60;
		background-color: #f0f7ff;
	}
`;

const defaultInitialValues = {
	// Basic Information
	vendor_type: '',
	vendor_name: '',
	legal_structure: '',
	vendor_email_id: '',
	primary_poc_name: '',
	primary_poc_contact: '',
	alternative_poc_name: '',
	alternative_poc_contact: '',
	vendor_address: '',
	pincode: '',
	zone: '',
	city: '',
	state: '',
	business_unit_id: 5,

	// Business Information
	pan_document_type: 'PAN',
	pan_number: '',
	pan_file: null,
	pan_file_key: null,
	gst_document_type: 'GST',
	gst_type: '',
	gst_number: '',
	gst_file: null,
	gst_file_key: null,
	aadhar_document_type: 'Aadhar',
	aadhar_number: '',
	aadhar_file: null,
	aadhar_file_key: null,
	udf_file: null,
	udf_file_key: null,
	fds_applicable: false,

	// Banking Information
	account_holder_name: '',
	account_number: '',
	bank_name: '',
	branch_name: '',
	ifsc_code: '',
	bank_document_file: null,
	bank_document_key: null,
	tds_applicable: false,
	vendor_registered_msme: false,
	msme_number: '',
	vrf_document_file: null,
	vrf_document_key: null,

	// Vehicle Information
	vehicles: [],

	// Serviceability Info
	type_of_movement: '',
	source_city: '',
	destination_city: '',
	routes: [],
};

/**
 * Approved bank details can never be edited again, so the Banking step is locked once
 * the vendor's bank details approval has been granted (approvals_v2, surfaced as
 * `bank_details_approval_status` on the vendor read + update responses).
 */
const BANKING_STEP_INDEX = 2;
const BANK_DETAILS_APPROVED = 'approved';

const STEP_NAMES = [
	'Basic Information',
	'Business Information',
	'Banking Information',
	'Vehicle Information',
	'Serviceability Info',
];

// Validation Schemas for each step
const BasicInfoValidationSchema = Yup.object().shape({
	vendor_type: Yup.string().required('Vendor type is required'),
	vendor_name: Yup.string()
		.required('Vendor name is required')
		.max(256, 'Vendor name must not exceed 256 characters'),
	legal_structure: Yup.string().required('Legal structure is required'),
	vendor_email_id: Yup.string()
		.required('Vendor email is required')
		.email('Invalid email format')
		.max(255, 'Email must not exceed 255 characters'),
	primary_poc_name: Yup.string()
		.required('Primary PoC name is required')
		.max(255, 'Name must not exceed 255 characters'),
	primary_poc_contact: Yup.string()
		.required('Primary PoC contact is required')
		.matches(/^[0-9]{10}$/, 'Contact must be a valid 10-digit phone number'),
	vendor_address: Yup.string().required('Vendor address is required'),
	pincode: Yup.string()
		.required('Pincode is required')
		.matches(/^[0-9]{6}$/, 'Pincode must be 6 digits'),
	zone: Yup.string().required('Zone is required'),
	city: Yup.string().required('City is required'),
	state: Yup.string().required('State is required'),
});

const BusinessInfoValidationSchema = Yup.object().shape({
	pan_number: Yup.string()
		.required('PAN number is required')
		.matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN format'),
	pan_file_key: Yup.string().required('Please upload the PAN file'),
	gst_type: Yup.string().required('GST type is required'),
	gst_number: Yup.string().when('gst_type', {
		is: (val) => val === 'Registered',
		then: (schema) =>
			schema
				.required('GST number is required')
				.matches(
					/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
					'Invalid GSTIN format'
				),
		otherwise: (schema) => schema.notRequired().nullable(),
	}),
	gst_file_key: Yup.string().when('gst_type', {
		is: (val) => val === 'Registered',
		then: (schema) => schema.required('Please upload the GST file'),
		otherwise: (schema) => schema.notRequired().nullable(),
	}),
	aadhar_number: Yup.string()
		.required('Aadhar number is required')
		.matches(/^[0-9]{12}$/, 'Aadhar must be 12 digits'),
	aadhar_file_key: Yup.string().required('Please upload the Aadhar file'),
});

const BankingInfoValidationSchema = Yup.object().shape({
	account_holder_name: Yup.string()
		.required('Account holder name is required')
		.max(255, 'Name must not exceed 255 characters'),
	account_number: Yup.string()
		.required('Account number is required')
		.matches(/^[0-9]{9,18}$/, 'Account number must be between 9 to 18 digits'),
	bank_name: Yup.string()
		.required('Bank name is required')
		.max(255, 'Bank name must not exceed 255 characters'),
	ifsc_code: Yup.string()
		.required('IFSC code is required')
		.matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format'),
	branch_name: Yup.string()
		.required('Branch name is required')
		.max(255, 'Branch name must not exceed 255 characters'),
	bank_document_key: Yup.string().required('Please upload the bank document'),
	msme_number: Yup.string()
		.nullable()
		.when('vendor_registered_msme', {
			is: true,
			then: (schema) =>
				schema.required('MSME number is required when vendor is registered as MSME'),
			otherwise: (schema) => schema.notRequired(),
		}),
	vrf_document_key: Yup.string().required('Please upload the VRF document'),
});

const ServiceabilityInfoValidationSchema = Yup.object().shape({
	// The input fields (type_of_movement, source_city, destination_city) are for adding new routes
	// They are not required for form submission - only the routes array matters
	routes: Yup.array()
		.min(1, 'At least one route must be added')
		.required('At least one route is required'),
});

const getValidationSchemaForStep = (step) => {
	switch (step) {
		case 0:
			return BasicInfoValidationSchema;
		case 1:
			return BusinessInfoValidationSchema;
		case 2:
			return BankingInfoValidationSchema;
		case 3:
			return Yup.object().shape({}); // No validation for vehicle step
		case 4:
			return ServiceabilityInfoValidationSchema;
		default:
			return Yup.object().shape({});
	}
};

export default function VendorFormV2({
	currentStep,
	onNext,
	onPrevious,
	onSubmit,
	totalSteps,
	submitting = false,
	initialValues: propInitialValues,
	isEditMode = false,
	editableStep = null,
	onEnableEdit,
	onCancelEdit,
	vendorId = null,
	onSave = null,
	savedVendorId = null,
	saving = false,
	vehiclesSaved = false,
	routesSaved = false,
}) {
	console.log('🔄 [VendorFormV2] Component loaded/re-rendered', {
		currentStep,
		isEditMode,
		totalSteps,
	});

	const initialValues =
		propInitialValues && typeof propInitialValues === 'object'
			? { ...defaultInitialValues, ...propInitialValues }
			: defaultInitialValues;

	const handleSubmit = (values, formikBag) => {
		console.log('🚀 [VendorFormV2] Form submitted', {
			currentStep,
			isEditMode,
			valuesCount: Object.keys(values).length,
		});

		if (!values || typeof values !== 'object') {
			console.error('❌ [VendorFormV2] Invalid form values');
			return;
		}

		// In edit mode, always call onSubmit (allows updating individual steps)
		if (isEditMode) {
			console.log('📝 [VendorFormV2] Edit mode - calling onSubmit');
			if (onSubmit && typeof onSubmit === 'function') {
				onSubmit(values, formikBag);
			}
			return;
		}

		// In create mode, only call onSubmit on final step
		if (currentStep === totalSteps - 1) {
			// On final step, pass all form values to onSubmit
			console.log('✅ [VendorFormV2] Final step - calling onSubmit with all values', {
				hasOnSubmit: typeof onSubmit === 'function',
				formValuesKeys: Object.keys(values),
			});
			if (onSubmit && typeof onSubmit === 'function') {
				console.log('🔥 [VendorFormV2] Calling CreateVendorPageV2.handleSubmit now...');
				onSubmit(values, formikBag);
			} else {
				console.error('❌ [VendorFormV2] onSubmit is not a function!', typeof onSubmit);
			}
			return;
		}

		console.log('➡️ [VendorFormV2] Not final step - calling onNext');
		if (onNext && typeof onNext === 'function') {
			onNext();
		}
	};

	// In edit mode: editableStep === null means all disabled, editableStep === currentStep means this step is editable
	const isCurrentStepEditable = !isEditMode || (isEditMode && editableStep === currentStep);
	const isCurrentStepDisabled =
		isEditMode && editableStep !== null && editableStep !== currentStep;

	// Edit mode only: during creation the Banking step is how the details get entered.
	const bankingLocked =
		isEditMode && propInitialValues?.bank_details_approval_status === BANK_DETAILS_APPROVED;

	const renderStepContent = () => {
		if (
			currentStep === null ||
			currentStep === undefined ||
			Number.isNaN(Number(currentStep))
		) {
			return (
				<BasicInformationStep isEditable={!isEditMode} isDisabled={isCurrentStepDisabled} />
			);
		}

		switch (currentStep) {
			case 0:
				return (
					<BasicInformationStep
						isEditable={isCurrentStepEditable}
						isDisabled={isCurrentStepDisabled}
					/>
				);
			case 1:
				return (
					<BusinessInformationStep
						isEditable={isCurrentStepEditable}
						isDisabled={isCurrentStepDisabled}
					/>
				);
			case 2:
				return (
					<BankingInformationStep
						isEditable={isCurrentStepEditable && !bankingLocked}
						isDisabled={isCurrentStepDisabled}
					/>
				);
			case 3:
				return (
					<VehicleInformationStep
						isEditable={isCurrentStepEditable}
						isDisabled={isCurrentStepDisabled}
					/>
				);
			case 4:
				return (
					<ServiceabilityInfoStep
						isEditable={isCurrentStepEditable}
						isDisabled={isCurrentStepDisabled}
						vendorId={vendorId}
						isEditMode={isEditMode}
					/>
				);
			default:
				return (
					<BasicInformationStep
						isEditable={!isEditMode}
						isDisabled={isCurrentStepDisabled}
					/>
				);
		}
	};

	const renderStepHeader = () => {
		if (!isEditMode) return null;

		const stepName = STEP_NAMES[currentStep] || 'Step';
		const isEditable = editableStep === currentStep;
		if (bankingLocked && currentStep === BANKING_STEP_INDEX) {
			return (
				<StepHeader>
					<StepTitle>{stepName}</StepTitle>
					<Tag color="warning" style={{ width: 'auto', marginRight: 0 }}>
						Approved — bank details cannot be edited
					</Tag>
				</StepHeader>
			);
		}

		return (
			<StepHeader>
				<StepTitle>{stepName}</StepTitle>
				{!isEditable ? (
					<EditButton
						icon={<EditOutlined />}
						onClick={() => onEnableEdit && onEnableEdit(currentStep)}
					>
						Edit
					</EditButton>
				) : (
					<EditButton onClick={() => onCancelEdit && onCancelEdit()}>Cancel</EditButton>
				)}
			</StepHeader>
		);
	};

	/**
	 * Render form buttons (Save, Next, Submit, etc.)
	 */
	const renderFormButtons = (
		validateForm,
		setTouched,
		values,
		setErrors,
		setFieldValue,
		submitForm
	) => {
		const isBankingLockedStep = bankingLocked && currentStep === BANKING_STEP_INDEX;
		const showButtons =
			!isEditMode || isBankingLockedStep || isCurrentStepEditable || editableStep === null;
		if (!showButtons) return null;

		const isFinalStep = currentStep === totalSteps - 1;
		const isViewMode = isEditMode && (editableStep === null || isBankingLockedStep);
		const isEditingStep =
			isEditMode &&
			!isBankingLockedStep &&
			editableStep !== null &&
			editableStep === currentStep;

		return (
			<ButtonContainer>
				<div>
					{currentStep > 0 && (
						<PreviousButton onClick={onPrevious}>Previous</PreviousButton>
					)}
				</div>
				<div style={{ display: 'flex', gap: '12px' }}>
					{renderSaveButton(
						isViewMode,
						isEditingStep,
						validateForm,
						setTouched,
						values,
						setErrors,
						setFieldValue
					)}
					{renderNextButton(
						isViewMode,
						isEditingStep,
						isFinalStep,
						validateForm,
						setTouched,
						values
					)}
					{renderSubmitButton(isFinalStep, submitForm)}
				</div>
			</ButtonContainer>
		);
	};

	/**
	 * Render Save button
	 * Show on steps 2, 3, 4:
	 * - In create mode: show on steps 2, 3, 4 (Banking, Vehicle, Serviceability)
	 * - In edit mode: show on steps 0, 1, 2, 3, 4 when step is being edited
	 */
	const renderSaveButton = (
		isViewMode,
		isEditingStep,
		validateForm,
		setTouched,
		values,
		setErrors,
		setFieldValue
	) => {
		// In create mode: only show Save button on steps 2, 3, 4 (not on Basic Info or Business Info)
		// In edit mode: show Save button on steps 0, 1, 2, 3, 4 when step is being edited
		const showSaveButton = isEditMode
			? isEditingStep && currentStep >= 0 && currentStep <= 4
			: currentStep >= 2 && currentStep <= 4;

		if (!onSave || isViewMode || !showSaveButton) return null;

		return (
			<SaveButton
				type="button"
				loading={saving}
				disabled={saving}
				onClick={() => {
					handleSaveClick(validateForm, setTouched, values, setErrors, setFieldValue);
				}}
			>
				Save
			</SaveButton>
		);
	};

	/**
	 * Render Next button
	 */
	const renderNextButton = (
		isViewMode,
		isEditingStep,
		isFinalStep,
		validateForm,
		setTouched,
		values
	) => {
		// Show Next button if: (create mode OR view mode OR editing step) AND not final step
		const shouldShow = (!isEditMode || isViewMode || isEditingStep) && !isFinalStep;
		if (!shouldShow) return null;

		const handleNext = () => {
			console.log('🖱️ [VendorFormV2] Next button clicked', {
				isViewMode,
				currentStep,
				isEditMode,
				editableStep,
			});
			if (isViewMode) {
				console.log('👁️ [VendorFormV2] View mode - calling onNext directly');
				onNext();
			} else {
				console.log('✏️ [VendorFormV2] Edit mode - calling handleNextClick');
				handleNextClick(validateForm, setTouched, values);
			}
		};

		return (
			<NextButton type="button" onClick={handleNext}>
				Next
			</NextButton>
		);
	};

	/**
	 * Render Submit button (final step in create mode)
	 */
	const renderSubmitButton = (isFinalStep, submitForm) => {
		if (isEditMode || !isFinalStep) return null;

		return (
			<NextButton type="button" disabled={submitting} onClick={async () => submitForm()}>
				{submitting ? 'Submitting...' : 'Submit'}
			</NextButton>
		);
	};

	/**
	 * Validate save requirements before navigation
	 */
	const validateSaveRequirements = () => {
		// In create mode, step 2 (Banking Info) requires vendor to be saved before navigation
		if (!isEditMode && currentStep === 2 && !savedVendorId) {
			message.error('You need to click the save button first');
			return false;
		}

		// In create mode, step 3 (Vehicle Information) requires vehicles to be saved before navigation
		if (!isEditMode && currentStep === 3 && !vehiclesSaved) {
			message.error('You need to click the save button first to save vehicles');
			return false;
		}

		// In create mode, step 4 (Serviceability Info) requires routes to be saved before navigation
		if (!isEditMode && currentStep === 4 && !routesSaved) {
			message.error('You need to click the save button first to save routes');
			return false;
		}

		return true;
	};

	/**
	 * Validate vehicle data for step 3
	 */
	const validateVehicleData = (values) => {
		if (currentStep !== 3) return true;

		const vehicles = values?.vehicles || [];
		if (vehicles.length === 0) {
			message.error('Please add at least one vehicle before proceeding');
			return false;
		}

		// Validate each vehicle has required fields
		const invalidVehicles = vehicles.filter((vehicle) => {
			const vehicleTypeId = vehicle.vehicle_type_id;
			return !vehicleTypeId || !vehicle.vehicle_number;
		});

		if (invalidVehicles.length > 0) {
			message.error(
				`Please ensure all vehicles have vehicle type and vehicle number. ${invalidVehicles.length} vehicle(s) are incomplete.`
			);
			return false;
		}

		return true;
	};

	/**
	 * Handle Next button click for intermediate steps
	 * Note: Final step and edit mode use form submit instead of this function
	 */
	const handleNextClick = async (validateForm, setTouched, values) => {
		console.log('📝 [VendorFormV2] Next button clicked, validating step', currentStep, {
			isEditMode,
			editableStep,
			currentStep,
		});

		// Check save requirements (only for create mode)
		if (!isEditMode && !validateSaveRequirements()) {
			console.log('❌ [VendorFormV2] Save requirements not met');
			return;
		}

		// Validate vehicle data for step 3
		if (!validateVehicleData(values)) {
			console.log('❌ [VendorFormV2] Vehicle data validation failed');
			return;
		}

		// Validate current step
		const validationErrors = await validateForm();

		if (Object.keys(validationErrors).length > 0) {
			// Debug: Log validation errors
			console.log('❌ [VendorFormV2] Validation errors:', validationErrors);

			// Mark all error fields as touched to show validation messages
			const touchedFields = {};
			Object.keys(validationErrors).forEach((field) => {
				touchedFields[field] = true;
			});
			setTouched(touchedFields);
			message.error('Please fix validation errors before proceeding');
			return;
		}

		console.log('✅ [VendorFormV2] Validation passed, moving to next step');

		// Move to next step
		if (onNext && typeof onNext === 'function') {
			console.log('🔄 [VendorFormV2] Calling onNext handler');
			onNext();
		} else {
			console.error('❌ [VendorFormV2] onNext is not a function!', typeof onNext);
		}
	};

	/**
	 * Handle Save button click
	 */
	const handleSaveClick = async (validateForm, setTouched, values, setErrors, setFieldValue) => {
		console.log('💾 [VendorFormV2] Save button clicked, validating step', currentStep);

		// Validate current step
		const validationErrors = await validateForm();

		if (Object.keys(validationErrors).length > 0) {
			console.log('❌ [VendorFormV2] Validation errors:', validationErrors);

			// Mark all error fields as touched to show validation messages
			const touchedFields = {};
			Object.keys(validationErrors).forEach((field) => {
				touchedFields[field] = true;
			});
			setTouched(touchedFields);
			return;
		}

		console.log('✅ [VendorFormV2] Validation passed, calling onSave');

		// Call onSave handler with setFieldValue for updating form values after save
		if (onSave && typeof onSave === 'function') {
			await onSave(values, setErrors, setFieldValue);
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={getValidationSchemaForStep(currentStep)}
			onSubmit={handleSubmit}
			enableReinitialize
			validateOnChange={false}
			validateOnBlur
		>
			{({ validateForm, setTouched, submitForm, values, setErrors, setFieldValue }) => (
				<StyledForm>
					{renderStepHeader()}
					{renderStepContent()}
					{renderFormButtons(
						validateForm,
						setTouched,
						values,
						setErrors,
						setFieldValue,
						submitForm
					)}
				</StyledForm>
			)}
		</Formik>
	);
}

VendorFormV2.propTypes = {
	currentStep: PropTypes.number.isRequired,
	onNext: PropTypes.func.isRequired,
	onPrevious: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	totalSteps: PropTypes.number.isRequired,
	submitting: PropTypes.bool,
	initialValues: PropTypes.object,
	isEditMode: PropTypes.bool,
	editableStep: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onEnableEdit: PropTypes.func,
	onCancelEdit: PropTypes.func,
	vendorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onSave: PropTypes.func,
	savedVendorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	saving: PropTypes.bool,
	vehiclesSaved: PropTypes.bool,
	routesSaved: PropTypes.bool,
};
